<template>
    <div v-loading="loading" class="compliance-impersonate-page">
        <h2 class="title typographies-heading-h2">
            <span>
                {{ $l10n.__('Mon Support Sellsy') }}
            </span>
        </h2>
        <h3 v-if="isConsentEnabled" class="subtitle typographies-heading-h3">
            <span>
                {{ $l10n.__('Autoriser Sellsy à acceder à mon compte') }}
            </span>
        </h3>
        <p
            v-if="isConsentEnabled"
            class="introduction typographies-paragraph-medium-regular"
            v-html="introductionContent"
        />
        <div v-if="isConsentEnabled" class="pending-authorizations">
            <Notification
                v-for="(pendingAuthorization, index) in pendingAuthorizations"
                :key="index"
                :authorization="pendingAuthorization"
                @accept-success="getUserAuthorizations"
                @refuse-success="getUserAuthorizations"
            />
            <div v-if="!pendingAuthorizations.length" class="pending-authorizations__empty">
                <CompassIcon icon="clock" />
                <p class="typographies-heading-h4">
                    {{ $l10n.__("Pas de demande d'accès en attente") }}
                </p>
            </div>
        </div>
        <section v-if="isLogActionsEnabled" class="sellsy-actions-history">
            <h3 class="subtitle typographies-heading-h3">
                <span>
                    {{ $l10n.__('Historique des actions Sellsy') }}
                </span>
            </h3>
            <p
                class="sellsy-actions-history__text typographies-paragraph-medium-regular"
                v-html="sellsyActionsText"
            />
            <div class="sellsy-actions-history__button">
                <template v-if="isAdmin">
                    <CompassMenu>
                        <CompassButton
                            :label="
                                $t.__(
                                    'pages.settings.complianceImpersonate.ComplianceImpersonate.download_actions',
                                )
                            "
                            :loading="downloadInProgress"
                            type="secondary"
                            dropdown
                        />
                        <template #dropdown>
                            <CompassDropdownOption
                                :label="
                                    $t.__(
                                        'pages.settings.complianceImpersonate.ComplianceImpersonate.from_your_account',
                                    )
                                "
                                @itemSelected="handleDownload('me')"
                            />
                            <CompassDropdownOption
                                :label="
                                    $t.__(
                                        'pages.settings.complianceImpersonate.ComplianceImpersonate.from_support',
                                    )
                                "
                                @itemSelected="handleDownload('support')"
                            />
                        </template>
                    </CompassMenu>
                </template>
                <template v-else>
                    <CompassButton
                        :label="
                            $t.__(
                                'pages.settings.complianceImpersonate.ComplianceImpersonate.download_actions',
                            )
                        "
                        :loading="downloadInProgress"
                        type="secondary"
                        leftIcon="download"
                        @click="handleDownload('me')"
                    />
                </template>
            </div>
        </section>
        <section v-if="isConsentEnabled" class="history-access-authorisations">
            <h3 class="subtitle typographies-heading-h3">
                <span>
                    {{ $l10n.__('Historique des autorisations d’accès') }}
                </span>
            </h3>
            <el-table
                v-if="authorizationsWithoutPending.length || loading"
                ref="tableHistoryAccessAuthorisations"
                data-test="tableHistoryAccessAuthorisations"
                :data="sortTableByDate"
                max-height="613"
                class="page-table"
                headerCellClassName="page-table__header, typographies-paragraph-medium-bold"
                rowClassName="page-table__row"
                cellClassName="page-table__cell"
            >
                <el-table-column
                    :label="
                        $t.__(
                            'pages.settings.complianceImpersonate.ComplianceImpersonate.permission_date',
                        )
                    "
                    prop="accepted-at"
                    className="page-table__header"
                >
                    <template #default="scope">
                        <span>
                            {{
                                scope.row.status !== 'refused'
                                    ? formatDate(scope.row.accepted_at)
                                    : '-'
                            }}
                        </span>
                    </template>
                </el-table-column>
                <el-table-column
                    :label="
                        $t.__(
                            'pages.settings.complianceImpersonate.ComplianceImpersonate.expiration_date',
                        )
                    "
                    prop="expired-at"
                    className="page-table__header"
                >
                    <template #default="scope">
                        <span>
                            {{ getDataExpiredAt(scope.row) }}
                        </span>
                    </template>
                </el-table-column>
                <el-table-column
                    :label="
                        $t.__(
                            'pages.settings.complianceImpersonate.ComplianceImpersonate.access_type',
                        )
                    "
                    prop="type"
                    className="page-table__header"
                >
                    <template #default="scope">
                        <span>
                            {{ formatAccessType(scope.row.type) }}
                        </span>
                    </template>
                </el-table-column>
                <el-table-column
                    :label="$t.__('common.labels.status')"
                    prop="status"
                    className="page-table__header"
                >
                    <template #default="scope">
                        <CompassTag type="dot" :color="dotColor(scope.row.status)">
                            {{ $l10n.__(statusText(scope.row.status)) }}
                        </CompassTag>
                    </template>
                </el-table-column>
                <el-table-column label="" className="page-table__header" align="right">
                    <template #default="scope">
                        <CompassButton
                            v-if="scope.row.status === 'active'"
                            ref="revokeButton"
                            :label="$t.__('common.actions.revoke_access')"
                            type="primary-destructive"
                            size="small"
                            @click="handleRevoke(scope.row.id)"
                        />
                    </template>
                </el-table-column>
            </el-table>
            <div v-else class="table__empty">
                <IllustrationAutorisationIcon class="table__empty--icon" />
                <span class="typographies-heading-h4">
                    {{ $l10n.__('Aucune autorisation en cours ou passée') }}
                </span>
            </div>
        </section>
        <RevokeDialog
            :visible="displayRevokeConfirmation"
            :uuid="authorizationId"
            @close="handleClose"
            @revoke-success="getUserAuthorizations"
        />
    </div>
</template>

<script>
import Feature from 'Services/Feature';
import Notification from './ImpersonateNotification.vue';
import RevokeDialog from './ImpersonateRevokeDialog.vue';
import IllustrationAutorisationIcon from 'vcomponents/icons/IconIllustrationAutorisation.vue';

import { getAuthorizations } from 'api/impersonate/impersonateService';
import { createNamespacedHelpers } from 'vuex';
import { downloadLogs } from 'api/impersonate/impersonateService';
const { mapGetters } = createNamespacedHelpers('user');

export default {
    components: {
        Notification,
        RevokeDialog,
        IllustrationAutorisationIcon,
    },
    data() {
        return {
            loading: true,
            authorizations: [],
            displayRevokeConfirmation: false,
            authorizationId: null,
            downloadInProgress: false,
        };
    },
    computed: {
        ...mapGetters(['getUser']),

        introductionContent() {
            return this.$l10n.__(
                "Afin de vous fournir une assistance plus efficace et personnalisée, nous avons besoin de votre autorisation pour nous connecter à votre compte Sellsy.%(br)s Vous pourrez révoquer cette autorisation à tout moment et consulter l'historique des accès à votre compte sur cette même page.",
                { br: '<br />' },
            );
        },
        pendingAuthorizations() {
            return this.authorizations.filter((item) => item.status === 'pending');
        },
        formatAccessType() {
            return (type) =>
                type === 'service'
                    ? this.$l10n.__('Prestation de services')
                    : this.$l10n.__('Support');
        },
        formatDate() {
            return (date) => {
                const momentDate = moment(date);
                if (momentDate.isValid()) {
                    return this.$l10n.__('%(date)s à %(time)s', {
                        date: moment(date).format('L'),
                        time: moment(date).format('LT'),
                    });
                }
                return '-';
            };
        },
        authorizationsWithoutPending() {
            return this.authorizations.filter((item) => item.status !== 'pending');
        },
        sortTableByDate() {
            const sortedData = [...this.authorizationsWithoutPending];
            return sortedData.sort((a, b) => new Date(b.accepted_at) - new Date(a.accepted_at));
        },
        sellsyActionsText() {
            return this.$l10n.__(
                "Retrouvez ci-dessous toutes les connexions et actions réalisées par l'équipe Sellsy sur votre compte.%(br)sNotez que nous limitons les actions sur votre compte autant que faire se peut.",
                { br: '<br />' },
            );
        },
        isConsentEnabled() {
            return Feature.isEnabled('impersonate-consent-check');
        },
        isLogActionsEnabled() {
            return Feature.isEnabled('impersonate-log-actions');
        },
        isAdmin() {
            return this.getUser.isAdmin;
        },
    },
    created() {
        this.getUserAuthorizations();
    },
    methods: {
        getDataExpiredAt(data) {
            if (data.delay.label === 'illimité') {
                return this.$t.__('complianceImpersonate.unlimited');
            }
            return this.formatDate(data.expired_at);
        },
        getUserAuthorizations() {
            if (!this.loading) {
                this.loading = true;
            }

            const userID = this.getUser.id;

            getAuthorizations(userID)
                .then((res) => (this.authorizations = res.data))
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$l10n.__('Une erreur est survenue, merci de réessayer'),
                        close: true,
                    });
                })
                .finally(() => (this.loading = false));
        },
        dotColor(status) {
            let color;
            switch (status) {
                case 'active':
                    color = 'green';
                    break;
                case 'refused':
                    color = 'gray';
                    break;
                case 'revoked':
                    color = 'red';
                    break;
                default:
                    color = 'green';
            }
            return color;
        },
        statusText(status) {
            switch (status) {
                case 'pending':
                    return this.$l10n.__('En attente');
                case 'active':
                    return this.$l10n.__('Autorisée');
                case 'expired':
                    return this.$l10n.__('Expirée');
                case 'refused':
                    return this.$l10n.__('Refusée');
                case 'revoked':
                    return this.$l10n.__('Révoquée');
                default:
                    return '-';
            }
        },
        handleRevoke(id) {
            this.displayRevokeConfirmation = true;
            this.authorizationId = id;
        },
        handleClose() {
            this.displayRevokeConfirmation = false;
            this.authorizationId = null;
        },
        handleDownload(type) {
            this.downloadInProgress = true;
            downloadLogs(type)
                .then(() => {
                    this.$pushNotification({
                        text: this.$l10n.__(
                            'Export en cours de traitement. Vous allez recevoir un email avec le lien de téléchargement lorsque le fichier sera prêt.',
                        ),
                        avatar: 'SE',
                        isSellsy: true,
                    });
                })
                .catch((error) => {
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: error,
                        close: true,
                    });
                })
                .finally(() => {
                    this.downloadInProgress = false;
                });
        },
    },
};
</script>

<style lang="scss">
/* overwrite style element lib */
.compliance-impersonate-page {
    .page-table {
        &:before {
            border-bottom: 1px solid var(--colors-common-border-default);
        }

        &__header {
            padding: 14px var(--spacing-3);
        }
        &__row {
            &:hover > td {
                background: var(--colors-common-background-hover) !important;
            }
        }
        .cell {
            padding: 0;
        }
        &__cell {
            padding: 14px var(--spacing-3);
        }
    }
    /* hidden message "no table data" from el-table when data load */
    .el-table__empty-block {
        display: none;
    }
}
</style>

<style lang="scss" scoped>
.compliance-impersonate-page {
    background-color: var(--colors-common-background-default);
    border: 1px solid var(--colors-common-border-default);
    border-radius: 4px;
    padding: var(--spacing-4);

    .title {
        align-items: center;
        display: flex;
        justify-content: space-between;
        margin: 0 0 var(--spacing-3);
    }

    .subtitle {
        margin: 0 0 var(--spacing-1);
    }

    .introduction {
        margin: 0 0 var(--spacing-3);
    }

    .sellsy-actions-history {
        margin: 0 0 var(--spacing-5);

        &__text {
            margin: 0 0 var(--spacing-1);
        }
    }

    .table {
        &__loader {
            align-items: center;
            display: flex;
            justify-content: center;
            padding: var(--spacing-3);
        }

        &__empty {
            align-items: center;
            border: 1px solid var(--colors-common-border-default);
            border-radius: 4px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            padding: var(--spacing-5);
            width: 100%;

            &--icon {
                margin-bottom: var(--spacing-3);
            }
        }
    }
}

.pending-authorizations {
    margin: 0 0 var(--spacing-5);

    &__empty {
        align-items: center;
        background: var(--colors-common-background-subtle);
        border-radius: 4px;
        display: flex;
        gap: var(--spacing-1);
        justify-content: flex-start;
        padding: var(--spacing-3);

        p {
            margin: 0;
        }
    }
}
</style>
